import { useRef } from "react";
import { Beef, ShieldCheck, ShoppingBag, User as UserIcon, Wallet } from "lucide-react";
import { useQrDataUrl } from "@/components/print/printUtils";
import SlipToolbar from "@/components/print/SlipToolbar";
import {
  SectionTitle, KV, Chip, SlipHeader, SlipFooter, GatePassPanel, SITE_URL,
} from "@/components/print/SlipParts";

/** Plain-text WhatsApp share message for a slip. */
function buildShareText(p) {
  return [
    `*${p.haatName} — ${p.shareTitle}*`,
    p.registrationNumber ? `Reg. No: ${p.registrationNumber}` : "",
    p.receiptNumber ? `Receipt: ${p.receiptNumber}` : "",
    p.passNumber ? `${p.isExitVariant ? "Exit Pass" : "Gate Pass"}: ${p.passNumber}` : "",
    p.amount ? `Amount: ₹ ${Number(p.amount).toLocaleString("en-IN")}` : "",
    p.sellerName ? `Seller: ${p.sellerName}` : "",
    p.isExitVariant && p.purchaserName ? `Purchaser: ${p.purchaserName}` : "",
    p.animalType ? `Animal: ${p.animalType}${p.animalBreed ? " · " + p.animalBreed : ""}` : "",
    `Date: ${p.dateStr} ${p.timeStr}`,
    "",
    `Powered by AnimalMela · ${SITE_URL}`,
  ].filter(Boolean).join("\n");
}

/* --- The unified premium mobile card ------------------------------------- */
/**
 * One card = Registration Slip + QR Gate Pass.
 * Max width 380px, rounded 16px, light shadow, 16px padding.
 * `variant`: "entry" (default) shows Entry Gate Pass. "exit" or `saleCompleted=true`
 *            reveals the Purchaser section and switches to an Exit Pass variant.
 */
export function MobileSlip({
  // header
  haatName = "AnimalMela",
  registrationNumber,
  timestamp,
  // seller
  sellerName,
  sellerMobile,
  sellerVillage,
  // purchaser (auto-revealed when saleCompleted OR variant==='exit')
  purchaserName,
  purchaserMobile,
  purchaserVillage,
  saleCompleted = false,
  // animal
  animalType,
  animalBreed,
  animalGender,
  animalColour,
  salePrice,
  // payment
  amount,
  paymentMode = "cash",
  receiptNumber,
  feeBreakdown,             // [{animal_type, quantity, price_per_animal, total}] — owner-priced fees
  // helper / subscriber
  helperName,
  subscriberName,
  // gate pass
  passNumber,
  qrPayload,
  animalId,
  entryStatus = "issued",   // issued | scanned
  exitStatus = "pending",   // pending | sold | unsold
  variant,                  // "entry" | "exit" — overrides labels
  // record timestamps (optional — for the TXT receipt)
  animalCount = 1,
  entryTime,
  exitTime,
  // share text override
  shareTitle,
}) {
  const ref = useRef(null);
  const qr = useQrDataUrl(qrPayload || passNumber || receiptNumber || "", 512);
  const when = timestamp ? new Date(timestamp) : new Date();
  const dateStr = when.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = when.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  // Auto-reveal purchaser only when we actually have a completed sale AND
  // purchaser data. Unsold exits should hide the section (no purchaser exists).
  const isSold = exitStatus === "sold";
  const isExitVariant = variant === "exit" || saleCompleted || exitStatus === "sold" || exitStatus === "unsold";
  const showPurchaser = isSold && (purchaserName || purchaserMobile || purchaserVillage);
  const passLabel = isExitVariant ? "Exit Pass" : "Entry Gate Pass";
  const resolvedShareTitle = shareTitle || (isExitVariant ? "Exit Pass" : "Registration Slip");

  const shareText = buildShareText({
    haatName, shareTitle: resolvedShareTitle, registrationNumber, receiptNumber, passNumber,
    isExitVariant, amount, sellerName, purchaserName, animalType, animalBreed, dateStr, timeStr,
  });
  const waHref = `https://wa.me/${sellerMobile ? sellerMobile.replace(/\D/g, "") : ""}?text=${encodeURIComponent(shareText)}`;

  // Lightweight TXT receipt — same data, no images.
  const fmtT = (iso) => (iso ? new Date(iso).toLocaleString("en-IN") : "");
  const txtText = [
    "AnimalMela",
    "----------------------------------------",
    `Pashu Haat: ${haatName || "-"}`,
    `Haat Owner: ${subscriberName || "-"}`,
    `Helper: ${helperName || "-"}`,
    `Registration No: ${registrationNumber || "-"}`,
    `Seller: ${sellerName || "-"}`,
    `Purchaser: ${purchaserName || "-"}`,
    `Animal Type: ${animalType || "-"}`,
    `Quantity: ${animalCount || 1}`,
    salePrice ? `Sale Price: Rs ${Number(salePrice).toLocaleString("en-IN")}` : "",
    `Registration Fee: Rs ${Number(amount || 0).toLocaleString("en-IN")}`,
    `Payment Status: PAID (${paymentMode})`,
    receiptNumber ? `Receipt No: ${receiptNumber}` : "",
    `Gate Pass No: ${passNumber || "-"}`,
    `Entry: ${entryStatus}${entryTime ? " · " + fmtT(entryTime) : ""}`,
    `Exit: ${exitStatus}${exitTime ? " · " + fmtT(exitTime) : ""}`,
    `Date: ${dateStr} ${timeStr}`,
    "----------------------------------------",
    "www.animalmela.com",
  ].filter(Boolean).join("\n");

  // Concise SMS summary (plain text — no WhatsApp bold markers).
  const smsText = shareText.replace(/\*/g, "");

  return (
    <div className="space-y-3">
      <SlipToolbar
        targetRef={ref}
        shareTitle={resolvedShareTitle}
        shareText={shareText}
        waHref={waHref}
        txtText={txtText}
        smsText={smsText}
        filename={`${passNumber || receiptNumber || registrationNumber || "animalmela"}.pdf`}
      />

      {/* Card */}
      <div className="overflow-auto rounded-3xl border bg-black/5 p-3">
        <div
          ref={ref}
          data-testid="mobile-slip"
          className="am-print-doc am-slip bg-white text-black mx-auto"
          style={{
            width: "100%",
            maxWidth: "380px",
            borderRadius: "16px",
            padding: "16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.05)",
            fontFamily: "'Manrope', system-ui, sans-serif",
          }}
        >
          <SlipHeader haatName={haatName} registrationNumber={registrationNumber} dateStr={dateStr} timeStr={timeStr} />

          {/* Seller */}
          <SectionTitle icon={UserIcon}>Seller</SectionTitle>
          <div className="space-y-0">
            <KV label="Name" value={sellerName} />
            <KV label="Mobile" value={sellerMobile ? `+91 ${sellerMobile}` : ""} mono />
            <KV label="Village" value={sellerVillage} />
          </div>

          {/* Purchaser — hidden until sale completed */}
          {showPurchaser && (
            <>
              <SectionTitle icon={ShoppingBag}>Purchaser</SectionTitle>
              <div className="space-y-0">
                <KV label="Name" value={purchaserName} />
                <KV label="Mobile" value={purchaserMobile ? `+91 ${purchaserMobile}` : ""} mono />
                <KV label="Village" value={purchaserVillage} />
              </div>
            </>
          )}

          {/* Animal — photo intentionally not displayed (kept in data for future use) */}
          <SectionTitle icon={Beef}>Animal</SectionTitle>
          <div>
            <KV label="Type" value={animalType} />
            <KV label="Breed" value={animalBreed} />
            <KV label="Gender" value={animalGender} />
            <KV label="Colour" value={animalColour} />
          </div>
          {salePrice ? (
            <div className="mt-2 flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2">
              <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-800">Sale Price</span>
              <span className="font-mono font-extrabold text-emerald-900">₹ {Number(salePrice).toLocaleString("en-IN")}</span>
            </div>
          ) : null}

          {/* Payment */}
          <SectionTitle icon={Wallet}>Payment</SectionTitle>
          {Array.isArray(feeBreakdown) && feeBreakdown.length > 0 && (
            <div className="rounded-xl border border-black/10 px-3 py-2 mb-2" data-testid="slip-fee-breakdown">
              <div className="text-[10px] uppercase tracking-widest font-bold text-black/50 mb-1">Registration Fee</div>
              {feeBreakdown.map((l) => (
                <div key={l.animal_type} className="flex justify-between text-[12px] py-0.5">
                  <span className="capitalize">{l.animal_type} × {l.quantity} @ ₹{Number(l.price_per_animal).toLocaleString("en-IN")}</span>
                  <span className="font-mono font-bold">₹ {Number(l.total).toLocaleString("en-IN")}</span>
                </div>
              ))}
              <div className="flex justify-between text-[12px] border-t border-black/10 mt-1 pt-1 font-extrabold">
                <span>Grand Total</span>
                <span className="font-mono">₹ {Number(amount || 0).toLocaleString("en-IN")}</span>
              </div>
            </div>
          )}
          <div className="rounded-xl bg-black/[0.03] border border-black/5 px-3 py-2 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-black/50">Amount</div>
              <div className="font-extrabold text-lg leading-tight">₹ {Number(amount || 0).toLocaleString("en-IN")}</div>
            </div>
            <div className="text-right">
              <Chip>{paymentMode}</Chip>
              <div className="mt-1 font-mono text-[11px] font-bold">{receiptNumber || "—"}</div>
            </div>
          </div>

          {/* Pashu Haat identity — server-derived (haat, owner, helper) */}
          <SectionTitle icon={ShieldCheck}>Pashu Haat</SectionTitle>
          <div className="space-y-0">
            <KV label="Pashu Haat" value={haatName} />
            <KV label="Haat Owner" value={subscriberName} />
            <KV label="Helper" value={helperName} />
          </div>

          {/* Gate Pass */}
          <SectionTitle>Gate Pass</SectionTitle>
          <GatePassPanel
            isExitVariant={isExitVariant}
            passLabel={passLabel}
            passNumber={passNumber}
            qr={qr}
            animalId={animalId}
            entryStatus={entryStatus}
            exitStatus={exitStatus}
          />

          <SlipFooter />
        </div>
      </div>
    </div>
  );
}
